import type React from 'react';
import type { SportEvent } from '../types/SportEventTypes';
import type {
  DescribeEventContext,
  SportEventModule,
  SportEventTypeConfig,
  StatDefinition,
} from './types';
import { NHL_SPORT_MODULE } from './nhl';
import { NBA_SPORT_MODULE } from './nba';
import { readPayload } from './shared';

/**
 * The ordered list of sport modules. Order matters only for shape-based
 * dispatch (parseEventWithAnyModule iterates until one returns non-null) —
 * since SES wire shapes are disjoint per sport, the order has no functional
 * effect today. Listed alphabetically by id for predictability.
 */
const ALL_MODULES: ReadonlyArray<SportEventModule> = [NBA_SPORT_MODULE, NHL_SPORT_MODULE];

/** Read-only list of every registered sport module. */
export function getAllSportModules(): ReadonlyArray<SportEventModule> {
  return ALL_MODULES;
}

/**
 * Look up a module by league name (case-insensitive). Returns undefined if
 * unrecognized — callers should fall back to NHL behavior or treat the game
 * as sport-less.
 */
export function getSportModule(leagueName: string | null | undefined): SportEventModule | undefined {
  if (!leagueName) return undefined;
  const upper = leagueName.toUpperCase();
  return ALL_MODULES.find((m) => m.id === upper);
}

/**
 * Shape-based dispatch: try every module's parser until one claims the event.
 * Returns the parsed event and the module that handled it, or both null if
 * no module recognized the shape (likely a malformed or new event type).
 *
 * This is the entry point for `parsePlayByPlayEvent` in sportEventUtils —
 * callers never need to know which sport they're parsing.
 */
export function parseEventWithAnyModule(
  rawEvent: any,
): { module: SportEventModule | null; event: SportEvent | null } {
  for (const module of ALL_MODULES) {
    const event = module.parseEvent(rawEvent);
    if (event) return { module, event };
  }
  return { module: null, event: null };
}

/**
 * Type-based dispatch: find the module that owns a given event-type string
 * (e.g. "goal" -> NHL, "basket_made_3pt" -> NBA). Used by description
 * generators and by filter UIs that need to look up icons / display names.
 */
export function getModuleForEventType(eventType: string | undefined): SportEventModule | undefined {
  if (!eventType) return undefined;
  return ALL_MODULES.find((m) => m.eventTypes.some((t) => t.type === eventType));
}

/**
 * Lookup the per-type config (icon, display name, fetch flag, etc.) for a
 * given event-type string. Returns undefined for unknown types.
 */
export function getEventTypeConfig(eventType: string | undefined): SportEventTypeConfig | undefined {
  const module = getModuleForEventType(eventType);
  return module?.eventTypes.find((t) => t.type === eventType);
}

/**
 * Every event-type string that shares an event type's filter group, including
 * the type itself. Returns just `[eventType]` for ungrouped types (each type
 * is its own singleton group). Powers the "one pill, many types" filter
 * behavior — e.g. NBA's "2-Pointer" pill selects both the made and missed
 * 2-point types. Returns `[]` for an unknown/empty type.
 */
export function getFilterGroupMembers(eventType: string | undefined): string[] {
  if (!eventType) return [];
  const config = getEventTypeConfig(eventType);
  const group = config?.filterGroup;
  if (!group) return [eventType];
  const module = getModuleForEventType(eventType);
  if (!module) return [eventType];
  return module.eventTypes.filter((t) => t.filterGroup === group).map((t) => t.type);
}

/**
 * The event type that owns the filter pill for a given type — i.e. the member
 * of its filter group flagged `showFilterPill: true`. For an ungrouped type
 * this is the type itself when it's pill-visible. Returns undefined when no
 * member is pill-visible (the type contributes no pill). Used to build the
 * pill list so any member present in the data surfaces the group's single pill.
 */
export function getFilterPillOwner(eventType: string | undefined): string | undefined {
  for (const member of getFilterGroupMembers(eventType)) {
    if (getEventTypeConfig(member)?.showFilterPill) return member;
  }
  return undefined;
}

/**
 * The per-player stats a given league tracks, in display order. Empty array
 * for an unrecognized league. Used by the stats row and the shift filter to
 * render whatever the active sport defines.
 */
export function getStatDefinitions(
  leagueName: string | null | undefined,
): ReadonlyArray<StatDefinition> {
  return getSportModule(leagueName)?.stats ?? [];
}

/**
 * The display label for a league's object of play ("Puck", "Ball", …). Falls
 * back to the generic "Ball/Puck" for an unrecognized league.
 */
export function getObjectOfPlayLabel(leagueName: string | null | undefined): string {
  return getSportModule(leagueName)?.objectOfPlayLabel ?? 'Ball/Puck';
}

/**
 * Attribute a single event to player stats, sport-agnostically. Resolves the
 * owning module by event type, then runs each of its StatDefinitions. Returns
 * a flat list of (player, stat key, value-to-add); callers accumulate it. A
 * single event can yield multiple rows (e.g. an NHL goal credits both the
 * scorer's `goals` and the assister's `assists`).
 */
export function attributeEventStats(
  event: SportEvent,
): Array<{ playerId: string; stat: string; value: number }> {
  const module = getModuleForEventType(readPayload(event).type);
  if (!module) return [];
  const out: Array<{ playerId: string; stat: string; value: number }> = [];
  for (const def of module.stats) {
    for (const { playerId, value } of def.attribute(event)) {
      out.push({ playerId, stat: def.key, value: value ?? 1 });
    }
  }
  return out;
}

/**
 * Aggregate every sport's event types. Used for backward compatibility by
 * legacy consumers that imported the flat EVENT_TYPE_CONFIG array. New code
 * should prefer the per-sport `module.eventTypes` to keep the filter UI
 * scoped to the active game's sport.
 */
export function getAggregatedEventTypes(): ReadonlyArray<SportEventTypeConfig> {
  return ALL_MODULES.flatMap((m) => m.eventTypes);
}

/**
 * The player ids (`id.main`) featured in an event, resolved sport-agnostically.
 * Routes to the module that owns the event's `payload.type` (e.g. NHL owns
 * 'goal'; NBA owns 'basket_made_3pt') and returns its featured participants.
 * Returns `[]` for types no module owns (custom events, period markers) so
 * callers can render "no featured players" without branching on sport.
 */
export function getFeaturedParticipantIds(event: SportEvent): string[] {
  const module = getModuleForEventType(readPayload(event).type);
  return module?.featuredParticipantIds(event) ?? [];
}

/**
 * Resolve a specialized row renderer for an event, if its owning sport module
 * declares one via `pickRenderer` (e.g. NBA scoring plays -> ScoreEventItem).
 * Returns undefined when the sport has no override for this event, so callers
 * fall back to the generic RegularEventItem.
 */
export function pickEventRenderer(event: SportEvent): React.ComponentType<any> | undefined {
  const module = getModuleForEventType(readPayload(event).type);
  return module?.pickRenderer?.(event);
}

/**
 * Try each module's describeEvent in turn. The first non-null result wins.
 * Returns the event's stored `description` as a final fallback so we never
 * render "undefined" in the UI.
 */
export function describeEventWithAnyModule(
  event: SportEvent,
  ctx: DescribeEventContext,
): string {
  const { type } = readPayload(event);
  const ownerByType = getModuleForEventType(type);
  if (ownerByType) {
    const desc = ownerByType.describeEvent(event, ctx);
    if (desc) return desc;
  }
  // Last-ditch scan in case payload.type isn't set (malformed event).
  for (const module of ALL_MODULES) {
    const desc = module.describeEvent(event, ctx);
    if (desc) return desc;
  }
  return event.description || 'No details';
}
